import CameraImagesM from "@common/camera/CameraImagesM"
import { StructuredCorrectionBlock } from "@common/components/correction/StructuredCorrectionBlock"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import React, { useContext, useState, useEffect, useRef, useCallback } from "react"
import { ActivityIndicator, ScrollView, View } from "react-native"
import { Badge } from "react-native-paper"
import { connect } from "react-redux"
import FilledButtonM from "../../../common/components/button/FilledButtonM"
import IconButtonM from "../../../common/components/button/IconButtonM"
import { ModalM } from "../../../common/components/modal/ModalM"
import TextM from "../../../common/components/text/TextM"
import { theme } from "../../../common/theme/theme"
import { cleanMediaUrl } from "../../../data/constants/apiConstants"
import { activitiesService } from "../../../data/services/activitiesService"
import { hasRole } from "../../../data/utility/ability"
import ChampionBubble from "../../champions/ChampionBubble"
import { samVariantForActivity } from "../../champions/ChampionImageM"
import { AudioRecordingContainer } from "../execute/AudioRecordingContainer"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"

const ActivityDetailsModal = (props) => {
    const {
        activity,
        visible,
        onClose,
        activityType = "photo",
        images = [],
        recordingURI = null,
        noLaunchButton = false,
        maxReached = false,
        onRetry = () => {},
        correctionStatus = null,
        correctionGenerating = false,
        correctionImage = null,
        correctionPoints = null,
        onRefresh = () => {},
        onContinue = null,
        inline = false,
    } = props
    const dimensions = useContext(DimensionsContext)

    // L'analyse de Sam, sous les images, dès qu'elle porte des points. La
    // séquence qui suit l'envoi d'une copie les relève elle-même et les passe
    // par `correctionPoints`.
    const points = correctionPoints ?? activity?.correction_points
    const showStructuredCorrection =
        correctionStatus === "completed" && points?.length > 0

    // Feedback state
    const [feedbackType, setFeedbackType] = useState(null)
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false)

    // Polling for correction generation
    const pollingIntervalRef = useRef(null)
    const pollingCountRef = useRef(0)
    const MAX_POLLING_COUNT = 18 // 90 seconds max

    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
            pollingIntervalRef.current = null
        }
        pollingCountRef.current = 0
    }, [])

    const startPolling = useCallback(() => {
        if (pollingIntervalRef.current) return
        pollingIntervalRef.current = setInterval(() => {
            pollingCountRef.current += 1
            if (pollingCountRef.current >= MAX_POLLING_COUNT) {
                stopPolling()
                return
            }
            onRefresh()
        }, 5000)
    }, [onRefresh, stopPolling])

    useEffect(() => {
        if (visible && correctionGenerating) {
            startPolling()
        } else {
            stopPolling()
        }
        return () => stopPolling()
    }, [visible, correctionGenerating, startPolling, stopPolling])

    // Load existing feedback when modal opens
    useEffect(() => {
        if (visible && activity?.id) {
            props.apiRequest(activitiesService.getActivityCorrectionFeedback, {
                activityId: activity.id,
            })
        }
    }, [visible, activity?.id])

    useApiStatus(
        activitiesService.getActivityCorrectionFeedback,
        null,
        false,
        (data) => {
            if (data?.feedback_type) {
                setFeedbackType(data.feedback_type)
            }
        }
    )

    useApiStatus(
        activitiesService.submitActivityCorrectionFeedback,
        null,
        true,
        () => {
            setFeedbackSubmitting(false)
        },
        () => {
            setFeedbackSubmitting(false)
        }
    )

    const handleFeedback = (type) => {
        if (!activity?.id || feedbackSubmitting) return
        setFeedbackSubmitting(true)
        setFeedbackType(type)
        props.apiRequest(activitiesService.submitActivityCorrectionFeedback, {}, {
            activityId: activity.id,
            feedbackType: type,
        })
    }

    // Prepare images with correction first if available
    const allImages = correctionImage
        ? [
              { uri: correctionImage, isCorrection: true },
              ...images.map((img) => ({ ...img, isCorrection: false })),
          ]
        : images.map((img) => ({ ...img, isCorrection: false }))

    const activityId = activity?.id

    const logAudio = activity?.activity_logs?.find(
        (al) => al?.action === "step_media_sent"
    )

    const reviewComment = activity?.review_comment

    const modalWidth =
        dimensions?.width > 800 ? dimensions.width / 2 : dimensions.width - 50

    const showRetryButton =
        !noLaunchButton && !maxReached && !!activity?.finished_at

    const body = (
            <View
                style={{ width: "100%", height: "100%", position: "relative" }}
            >
                <ScrollView
                    style={{ width: "100%" }}
                    contentContainerStyle={{
                        alignItems: "center",
                    }}
                    showsVerticalScrollIndicator={true}
                >
                    <View style={{ width: "100%" }}>
                        <View
                            style={{
                                width: "100%",
                                marginBottom: 16,
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <View
                                style={{
                                    flex: 1,
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <TextM
                                    fontWeight="ExtraBold"
                                    fontSize={24}
                                    color={theme.colors.primary}
                                    style={{
                                        flex: 1,
                                        textAlign: "center",
                                        marginBottom: 4,
                                    }}
                                    wrap
                                >
                                    {"Mon Rendu".toUpperCase()}
                                </TextM>
                            </View>
                            <View style={{ alignItems: "flex-end" }}>
                                <IconButtonM
                                    icon="times"
                                    iconColor={theme.colors.primary}
                                    size={32}
                                    onPress={onClose}
                                />
                            </View>
                        </View>
                        {correctionGenerating && (
                            <ChampionBubble
                                name={"sam"}
                                variant={"main"}
                                championPosition="left"
                                texts={[
                                    "Je suis en train de corriger ton travail, tiens-toi prêt !",
                                ]}
                                onNextPress={() => {}}
                                width={modalWidth}
                            />
                        )}
                    </View>

                    {!correctionGenerating && !correctionImage && !!reviewComment && (
                        <ChampionBubble
                            name={"sam"}
                            variant={samVariantForActivity(activity)}
                            noAnimation={true}
                            championPosition="left"
                            texts={[reviewComment]}
                            onNextPress={() => {}}
                            width={modalWidth}
                        />
                    )}

                    {correctionGenerating ? (
                        <View
                            style={{
                                marginVertical: 20,
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <ActivityIndicator
                                animating={true}
                                size="large"
                                color={theme.colors.primary}
                            />
                            <TextM
                                style={{ marginTop: 10 }}
                                color={theme.colors.grey}
                            >
                                Génération de la correction...
                            </TextM>
                        </View>
                    ) : props.getActivitiesRequest &&
                      !(allImages.length > 0 || recordingURI) ? (
                        <ActivityIndicator
                            animating={true}
                            style={{
                                marginVertical: 10,
                            }}
                            size="large"
                            color={theme.colors.primary}
                        />
                    ) : activityType === "photo" && allImages.length > 0 ? (
                        <>
                            <CameraImagesM
                                images={allImages}
                                activityId={activityId}
                                style={{
                                    marginVertical: 20,
                                    width: "100%",
                                }}
                                noButton
                                readonly
                                noResend
                                longPressDisabled
                                scrollToStart={!!correctionImage}
                            />
                            {showStructuredCorrection && (
                                <View
                                    style={{
                                        width: "100%",
                                        paddingHorizontal: 10,
                                    }}
                                >
                                    <StructuredCorrectionBlock
                                        points={points}
                                        title="Analyse de ta copie"
                                    />
                                </View>
                            )}
                            {correctionImage && (
                                <View
                                    style={{
                                        flexDirection: "row",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        marginBottom: 16,
                                        gap: 20,
                                    }}
                                >
                                    <TextM
                                        fontSize={12}
                                        color={theme.colors.grey}
                                    >
                                        La correction t&apos;a aidé ?
                                    </TextM>
                                    <IconButtonM
                                        icon="thumbs-up"
                                        iconColor={
                                            feedbackType === "positive"
                                                ? theme.colors.success
                                                : theme.colors.grey
                                        }
                                        size={24}
                                        onPress={() =>
                                            handleFeedback("positive")
                                        }
                                        disabled={feedbackSubmitting}
                                        style={{
                                            backgroundColor:
                                                feedbackType === "positive"
                                                    ? theme.colors.successLight
                                                    : "transparent",
                                        }}
                                    />
                                    <IconButtonM
                                        icon="thumbs-down"
                                        iconColor={
                                            feedbackType === "negative"
                                                ? theme.colors.error
                                                : theme.colors.grey
                                        }
                                        size={24}
                                        onPress={() =>
                                            handleFeedback("negative")
                                        }
                                        disabled={feedbackSubmitting}
                                        style={{
                                            backgroundColor:
                                                feedbackType === "negative"
                                                    ? theme.colors.errorLight
                                                    : "transparent",
                                        }}
                                    />
                                </View>
                            )}
                        </>
                    ) : activityType === "audio" && recordingURI ? (
                        <View
                            style={{
                                justifyContent: "center",
                                alignItems: "center",
                                width: "100%",
                                padding: 16,
                            }}
                        >
                            <AudioRecordingContainer
                                recordingURI={cleanMediaUrl(recordingURI)}
                                duration={logAudio?.step_data?.chrono_duration}
                                readOnly
                            />
                        </View>
                    ) : (
                        <View style={{ padding: 20, alignItems: "center" }}>
                            <TextM>
                                Aucun travail n&apos;a été soumis pour cette
                                activité.
                            </TextM>
                        </View>
                    )}

                    {showRetryButton && !props.isParentMode && (
                        <View
                            style={{
                                width: "100%",
                                paddingHorizontal: 10,
                                marginTop: 10,
                                marginBottom: 20,
                            }}
                        >
                            <FilledButtonM
                                color={theme.colors.grey}
                                onPress={onRetry}
                                style={{
                                    width: "100%",
                                }}
                                icon={"redo"}
                                loading={props.activityTodoAsStudentRequest}
                                label="Refaire l'activité"
                            />
                        </View>
                    )}

                    {onContinue && (
                        <View
                            style={{
                                width: "100%",
                                paddingHorizontal: 10,
                                marginTop: 10,
                                marginBottom: 20,
                            }}
                        >
                            <FilledButtonM
                                color={theme.colors.success}
                                onPress={onContinue}
                                style={{
                                    width: "100%",
                                }}
                                icon={"play"}
                                label="J'ai compris"
                            />
                        </View>
                    )}
                </ScrollView>
            </View>
    )

    // Sur place, sans fenêtre : en live, la correction se lit dans l'écran
    // d'activité, sous la vignette du tuteur et au-dessus de la barre.
    if (inline) return visible ? body : null

    return (
        <ModalM
            style={{
                width: dimensions?.width > 800 ? "60%" : "90%",
                height: "80%",
                alignItems: "center",
            }}
            visible={visible}
            onDismiss={onClose}
        >
            {body}
        </ModalM>
    )
}

const mapStateToProps = (state) => {
    return {
        activityTodoAsStudentRequest: state.api.activityTodoAsStudent?.request,
        activityTodoAsTutorRequest: state.api.activityTodoAsTutor?.request,
        readReviewRequest: state.api.readReview?.request,
        getActivitiesRequest: state.api.getActivities?.request,
        isParentMode: state.data?.family?.isParentMode,
        user: state.data.currentUser?.object,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ActivityDetailsModal)
